/**
 * @file BackdropTab.tsx
 * @description Вкладка «Фон и рамка» (данные — backdrop/frame) оформления
 * сайта (phase19.16 Р1; SB11, SB89, SB82; uiux-design-r1 §1).
 *
 * SB122 (uiux-settings-groups §5 шаг 3): группы-карточки, панель
 * `backdrop` (память `commora.settings-groups.backdrop`):
 *  - «Картинка фона» (`image`) — картинка из медиатеки под всеми блоками;
 *  - «Рисунки» (`decor`) — до трёх украшений точкой по окну;
 *  - «Свечение» (`haze`, SB120 этап 2) — сила и цвет пятен по углам;
 *  - «Раскладка сайта» (`frame`) — во всю ширину или рамкой по центру.
 * Пустые группы не прячутся — внутри строка-подсказка.
 *
 * SB120 этап 2 (uiux-sb120-stage2 §2): всё числами; ползунки свечения и
 * рамки — живьём на канве до отпускания (`useThemePreview`).
 */

import type { SiteTheme } from "@chaiberi/shared";

import { SettingsGroup, SettingsGroups } from "../../ui/settings-groups";
import { BackdropDecorGroup } from "./BackdropDecorGroup";
import { BackdropFrameGroup } from "./BackdropFrameGroup";
import { HazeGroup } from "./HazeGroup";
import { PageBgImageFields } from "./PageBgImageFields";
import { PAGE_BG_IMAGE_HINT } from "./PageBgImageSection";
import { useThemePreview } from "../../../hooks/site-view/useThemePreview";
import { pageBgSummary } from "../../../lib/site-view/backdrop-summary";

interface Props {
  theme: SiteTheme;
  onBackdrop: (patch: Partial<SiteTheme["backdrop"]>) => void;
  onFrame: (patch: Partial<SiteTheme["frame"]>) => void;
}

export function BackdropTab({ theme, onBackdrop, onFrame }: Props) {
  const { backdrop, frame } = theme;
  const preview = useThemePreview(theme);
  return (
    <SettingsGroups panel="backdrop">
      <SettingsGroup id="image" title="Картинка фона" tocLabel="Картинка" hint={PAGE_BG_IMAGE_HINT} summary={pageBgSummary(backdrop.image)}>
        <PageBgImageFields image={backdrop.image} onBackdrop={onBackdrop} />
      </SettingsGroup>
      <BackdropDecorGroup backdrop={backdrop} onBackdrop={onBackdrop} />
      <HazeGroup haze={backdrop.haze} colors={theme.colors} onHaze={(haze) => onBackdrop({ haze })} onPreview={(haze) => preview("backdrop", { haze })} />
      <BackdropFrameGroup frame={frame} onFrame={onFrame} onPreview={(patch) => preview("frame", patch)} />
    </SettingsGroups>
  );
}
